/**
 * Christmas mantel ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasMantelIdeasPack: ToilePack = {
    slug: 'christmas-mantel-ideas',
    category: 'season',
    name: 'Christmas mantel ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'garland', label: 'Garland', subject: 'a mantel with a full garland and lights'},
        {key: 'stockings', label: 'Stockings', subject: 'a mantel with stockings'},
        {key: 'candles', label: 'Candles', subject: 'a mantel with candles and a mirror'},
    ],
    styles: [
        {key: 'farmhouse', label: 'Farmhouse', style: 'a white-painted brick fireplace in farmhouse style, soft daylight from camera-left, a warm balance of light and soft shadow', palette: 'white, pine green, red plaid and natural wood', framing: 'eye-level, the whole mantel and fireplace centered'},
        {key: 'elegant', label: 'Elegant', style: 'a carved marble fireplace in a classic room at evening, fire glow in the hearth and warm light from camera-right, a dark-to-light balance', palette: 'deep green, gold and cream marble', framing: 'eye-level, the mantel centered with the room dim around it'},
        {key: 'modern', label: 'Modern', style: 'a plain modern fireplace with a minimal floating mantel, cool soft daylight from camera-right, a pale airy balance with soft gray shadow', palette: 'white, black, eucalyptus and pale wood', framing: 'eye-level, generous calm wall around the mantel'},
    ],
    directions: ['no fireplace', 'red berries', 'pastel colors'],
    directionHint: 'try: no fireplace',
    seo: {
        title: 'Christmas Mantel Ideas: Garland, Stockings, Candles',
        metaDescription: 'Nine photos of a Christmas mantel dressed with garland, stockings or candles, over a farmhouse, elegant and modern fireplace.',
        h1: 'Christmas mantel ideas: garland, stockings, candles',
        tagline: 'Three mantel looks, each shown over a farmhouse, an elegant and a modern fireplace.',
        intro: 'Three ways to dress a fireplace mantel for Christmas: a full garland with lights, a row of stockings, and a mantel with candles and a mirror. Each is shown over a white farmhouse brick fireplace, a carved marble one with the fire lit, and a plain modern one. Use them to match a mantel idea to the fireplace you have. Type a twist, such as red berries or pastel colors, and the mantel is asked to follow it.',
        useCases: [
            'dressing a mantel for the season',
            'a shopping list for garland and candles',
            'a plan for a holiday party room',
            'a mantel refresh for winter',
        ],
        faq: [
            {q: 'What is on the mantel in each picture?', a: 'A full garland with lights, a row of stockings, and candles with a mirror.'},
            {q: 'Does the look depend on the fireplace?', a: 'Each mantel look is shown on three fireplaces: a white farmhouse brick one, a carved marble one and a plain modern one.'},
            {q: 'Can I ask for a mantel without a fireplace?', a: 'Yes. Type a twist, such as no fireplace, and the page asks for a mantel that follows it.'},
        ],
    },
}
